import * as React from 'react';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import Select, { ValueType } from 'react-select';
import { Button, Form, Input } from 'reactstrap';
import { Dispatch } from 'redux';
import { Application } from '../../clients/aasServerClient.generated';
import { clearSingleApplication, copySingleApplication, 
    createSingleApplication, newSingleApplication } from '../../stores/applications/actionCreators';
import { getSingleApplication } from '../../stores/applications/selectors';
import { isLoading } from '../../stores/navigation/selectors';
import { RootState } from '../../stores/store';
import SideModal from '../common/modals/SideModal';
import { LoadingButton } from '../navigation/LoadingButton';
import LoadingScreen from '../navigation/LoadingScreen';
import ServerSelect from '../servers/ServerSelect';
import WorkspaceSelect from '../workspaces/WorkspaceSelect';
import ApplicationProperties from './ApplicationProperties';

interface Props extends LocalizeContextProps, RouteComponentProps<{ id: string | undefined }> {
    isLoading: boolean;
    application: {
        loaded: boolean;
        saved: boolean;
        application?: Partial<Application>;
    };

    copySingleApplication: (id: string) => void;
    newSingleApplication: () => void;
    clearSingleApplication: () => void;
    createSingleApplication: (
        application: Partial<Application>,
        server: string,
        workspaceId?: string,
    ) => void;
}

interface State {
    application?: Partial<Application>;
    serverSelection?: { label: string, value: string };
    workspaceSelection?: { label: string, value: string };

    isCopy: boolean;
}

class NewApplicationPage extends React.Component<Props, State> {

    public constructor(props: Props) {
        super(props);

        const id = this.props.match.params.id;
        if (id) {
            this.state = {
                application: undefined,
                isCopy: true,
            };
            this.props.clearSingleApplication();
            this.props.copySingleApplication(id);
        } else {
            this.state = {
                application: undefined,
                isCopy: false,
            };
            this.props.clearSingleApplication();
            this.props.newSingleApplication();
        }
    }

    public componentDidUpdate(prevProps: Props) {
        if (!prevProps.application.loaded && this.props.application.loaded) {
            this.setState({
                application: this.props.application.application,
            });
        }
        if (!prevProps.application.saved && this.props.application.saved) {
            this.closePage();
        }
    }
    public modalRef = React.createRef<HTMLDivElement>();
    public render() {
        const t = this.props.translate;
        return (
            <SideModal
                isOpen={true}
                toggle={this.closePage}
                headerText={this.state.isCopy ? t('applications.edit.header.copy') as string
                    : t('applications.edit.header.add') as string}
                footer={this.renderFooter()}
                objectRef={this.modalRef}
            >
                {this.props.application.loaded && this.props.application.application ? (
                    <Form>
                        <h3 className="mb-4 mt-2">{t('applications.edit.section.details')}</h3>
                        <ApplicationProperties
                            type="new"
                            application={this.props.application.application}
                            updateApplication={this.updateApplication}
                        />
                        {this.props.application.application!.server ? (
                            <React.Fragment>
                                <hr className="fill-modal" />
                                <h3 className="mb-4 mt-4">{t('applications.edit.section.server')}</h3>
                                <ServerSelect
                                    type="single"
                                    selected={this.props.application.application.server.hostname}
                                    updateSelection={this.updateServerSelection}
                                    selection={this.state.serverSelection}
                                />
                            </React.Fragment>
                        ) : null}
                        {this.props.application.application!.workspace ? (
                            <React.Fragment>
                                <hr className="fill-modal" />
                                <h3 className="mb-4 mt-4">{t('applications.edit.section.workspace')}</h3>
                                <WorkspaceSelect
                                    selected={this.props.application.application.workspace.id}
                                    updateSelection={this.updateWorkspaceSelection}
                                    selection={this.state.workspaceSelection}
                                />
                            </React.Fragment>
                        ) : null}
                    </Form>
                ) : (
                        <LoadingScreen text={t('loading.loading') as string} />
                    )}
            </SideModal>
        );
    }

    private updateApplication = (application: Partial<Application>) => {
        this.setState({
            application,
        });
    }

    private updateServerSelection = (selection: ValueType<{ label: string, value: string }> |
    { label: string, value: string }) => {
        this.setState({ serverSelection: selection as { label: string, value: string } });
    }

    private updateWorkspaceSelection = (selection: ValueType<{ label: string, value: string }> |
    { label: string, value: string }) => {
        this.setState({ workspaceSelection: selection as { label: string, value: string } });
    }

    private closePage = () => {
        this.props.history.push('/applications');
    }

    private renderFooter = (): React.ReactNode => {
        const t = this.props.translate;
        return (
            <React.Fragment>
                <LoadingButton
                    loading={this.props.isLoading}
                    color="success"
                    onClick={this.createApplication}
                    style={{ width: '200px' }}
                >
                    {t('applications.edit.button.save')}
                </LoadingButton>
            </React.Fragment>
        );
    }

    private createApplication = () => {
        const application = this.state.application;
        const workspaceId = this.state.workspaceSelection?.value;
        const server = this.state.serverSelection?.value || '';
        const serverAliasValue = application!.server_alias || '';
        application!.server_alias = serverAliasValue;
        this.props.createSingleApplication(application!, server, workspaceId);
    }
}

const mapStateToProps = (state: RootState) => ({
    application: getSingleApplication(state),
    isLoading: isLoading(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    clearSingleApplication: () => dispatch(clearSingleApplication()),
    copySingleApplication: (id: string) => dispatch(copySingleApplication(id)),
    createSingleApplication: (
        application: Partial<Application>,
        server?: string,
        workspaceId?: string,
    ) => dispatch(createSingleApplication(application, server, workspaceId)),
    newSingleApplication: () => dispatch(newSingleApplication()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withLocalize(NewApplicationPage)));